/* eslint no-eval: 0 */
import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { CardHeader, Card, CardContent, Typography } from '@material-ui/core';
import * as actions from '../../../../store';
import { useDispatch, useSelector } from 'react-redux';
import DensitySection from './Density/DensitySection';
import DiagnosticRevisionTable from './Diagnostic/DiagnosticRevisionTable';
import QualityView from '../QualityView/QualityView';
import { useTranslation } from 'react-i18next';
import { ReviewerSelector } from '../Summary/SummaryCommon/ReviewerSelector';
import { getCurrentModuleStatus } from '../Summary/SummaryCommon/SummaryCommon';
import { useDiagnosticsData } from '../Summary/Diagnostics/useDiagnosticsData';
import { useDiagnosticsHandlers } from '../Summary/Diagnostics/useDiagnosticsHandlers';
import { useDiagnosticsEffects } from '../Summary/Diagnostics/useDiagnosticsEffects';

const useStyles = makeStyles((theme) => ({
  danai_main_container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '100%',
  },

  density_container: {
    width: '100%',
    margin: '2% 2% 0 2%',
  },

  diagnostic_container: {
    height: '100%',
    width: '100%',
    maxHeight: '85vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarGutter: 'stable',
    padding: '0',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[400],
      borderRadius: '2px',
    },
  },

  quality_container: {
    width: '100%',
    height: '86vh',
  },

  quality_container_revising: {
    width: '100%',
    height: '90vh',
  },

  danai_head_container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    margin: '0',
    padding: '0 16px 0 8px',
    height: '38px',
    background: theme.palette.background.lighterBg,
  },

  danai_title: {
    letterSpacing: '0.2rem',
    color: theme.palette.texts.primary,
    fontSize: theme.fonts.responsiveBig,
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
  },
}));

function DanaiTab() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const results = useSelector((state) => state.results);

  // Store state
  const { tabsIndex } = useSelector((store) => store.gui);
  const { zoomedProjection } = useSelector((store) => store.gui);
  const { premium_features, user_settings } = useSelector((state) => state.global_settings);
  const {
    densityInRevisionMode,
    diagnosticsInRevisionMode,
    qualityInRevisionMode,
    localChangesDensity,
    localChangesQuality,
    localChangesMicrocalc,
    localChangesOpacities,
  } = useSelector((state) => state.labeling);
  const { diagnostics_microcalc_module, diagnostics_opacities_module, data_annotation } = premium_features;
  const {
    projections: projData,
    applied_density,
    confirmed_density,
    shown_density,
    applied_microcalc,
    applied_opacities,
    confirmed_microcalc,
    confirmed_opacities,
    shown_microcalc,
    shown_opacities,
  } = useSelector((state) => state.results);

  const isDensityConfirmed = applied_density === 'bbox' && confirmed_density;
  const isDensityRelabeled = confirmed_density && applied_density !== 'bbox';
  const isMicrocalcConfirmed = applied_microcalc === 'bbox' && confirmed_microcalc;
  const isMicrocalcRelabeled = confirmed_microcalc && applied_microcalc !== 'bbox';
  const isOpacityConfirmed = applied_opacities === 'bbox' && confirmed_opacities;
  const isOpacityRelabeled = confirmed_opacities && applied_opacities !== 'bbox';

  const shownDensity = shown_density || applied_density;
  const shownMicrocalc = shown_microcalc || applied_microcalc;
  const shownOpacities = shown_opacities || applied_opacities;

  const someLocalChanges =
    localChangesDensity || localChangesQuality || localChangesMicrocalc || localChangesOpacities;

  const [densityPopperOpen, setDensityPopperOpen] = useState(false);
  const [diagnosticsPopperOpen, setDiagnosticsPopperOpen] = useState(false);

  // Custom hooks
  const diagnosticsData = useDiagnosticsData(t, theme, zoomedProjection);

  useEffect(() => {
    dispatch(actions.setExaminationListDrawer(false));
  }, []);

  useEffect(() => {
    if (!shown_density) dispatch(actions.setShownDensity(applied_density));
    if (!shown_microcalc) dispatch(actions.setShownMicrocalc(applied_microcalc));
    if (!shown_opacities) dispatch(actions.setShownOpacities(applied_opacities));
  }, [applied_density, applied_microcalc, applied_opacities, results]);

  useEffect(() => {
    if (
      (tabsIndex === 2 && !qualityInRevisionMode && !diagnosticsInRevisionMode && !densityInRevisionMode) ||
      !results
    ) {
      dispatch(actions.setTabsIndex(0));
    }
  }, [tabsIndex, results]);

  return (
    <Card
      style={{
        height: '100%',
        border: 'none !important',
        display: 'flex',
        flexDirection: 'column',
        padding: '0',
        justifyContent: 'space-between',
        border: '2px solid #436b6f',
      }}
      elevation={0}>
      {!qualityInRevisionMode && (
        <CardHeader
          style={{
            padding: 0,
            margin: 0,
            height: '36px',
            borderRadius: '5px 5px 0 0',
          }}
          title={
            <div className={classes.danai_head_container}>
              <Typography className={classes.danai_title} paragraph={false}>
                DANAI - {densityInRevisionMode ? t('b_box.b_density') : t('b_box.b_diagnose')}
              </Typography>
              {!someLocalChanges && (
                <ReviewerSelector
                  type={densityInRevisionMode ? 'density' : 'diagnostics'}
                  shownContent={
                    densityInRevisionMode
                      ? shown_density
                      : diagnosticsData.microcalcShown || diagnosticsData.opacitiesShown
                  }
                  appliedContent={
                    densityInRevisionMode
                      ? applied_density
                      : diagnosticsData.applied_microcalc || diagnosticsData.applied_opacities
                  }
                  isConfirmed={
                    densityInRevisionMode ? isDensityConfirmed : isMicrocalcConfirmed || isOpacityConfirmed
                  }
                  isRelabeled={
                    densityInRevisionMode ? isDensityRelabeled : isMicrocalcRelabeled || isOpacityRelabeled
                  }
                  classes={classes}
                  onPopperClose={() => {
                    densityInRevisionMode ? setDensityPopperOpen(false) : setDiagnosticsPopperOpen(false);
                  }}
                />
              )}
            </div>
          }
        />
      )}
      <CardContent
        style={{
          padding: 0,
          height: '100%',
        }}>
        {/* b-Density  revision*/}
        <div
          className={classes.danai_main_container}
          style={{ padding: qualityInRevisionMode ? '0' : '16px 8px' }}>
          {densityInRevisionMode && (
            <div className={classes.density_container} style={{ height: densityInRevisionMode && '80vh' }}>
              <div>
                <DensitySection />
              </div>
            </div>
          )}

          {/* b-Diagnose revision*/}
          {diagnosticsInRevisionMode && (
            <div className={classes.diagnostic_container}>
              <DiagnosticRevisionTable />
            </div>
          )}

          {/* b-Quality revision*/}
          {qualityInRevisionMode && (
            <div className={tabsIndex === 2 ? classes.quality_container_revising : classes.quality_container}>
              <QualityView />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default DanaiTab;
